import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { srConfig, email } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledContactSection = styled.section`
  max-width: 900px;
  margin: 0 auto;
  padding: 20px 0 0px !important;
  scroll-margin-top: 20px !important;
  position: relative;

  @media (max-width: 768px) {
    min-height: auto !important;
    display: block !important;
  }

  .numbered-heading {
    margin-bottom: 60px;
    font-size: clamp(30px, 5vw, 50px);
    text-align: left;

    &::before {
      display: none;
    }
  }

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 60%;
    height: 60%;
    background: var(--gradient);
    filter: blur(120px);
    opacity: 0.1;
    z-index: -1;
    border-radius: 50%;
  }

  .contact-card {
    text-align: center;
    background: rgba(255, 255, 255, 0.03);
    backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    padding: 80px 40px;
    box-shadow: 0 10px 30px -15px rgba(0, 0, 0, 0.5);
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-5px);
      background: rgba(255, 255, 255, 0.06);
      border-color: rgba(255, 42, 42, 0.95);
      box-shadow: 0 20px 40px -15px rgba(255, 42, 42, 0.25), 0 0 20px 2px rgba(255, 42, 42, 0.2);
    }

    @media (max-width: 768px) {
      padding: 50px 20px;
    }
  }

  .overline {
    display: inline-block;
    margin-bottom: 25px;
    color: #ff4a4a;
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
    font-weight: 600;
    letter-spacing: 3px;
    text-transform: uppercase;
    background: rgba(255, 42, 42, 0.08);
    border: 1px solid rgba(255, 42, 42, 0.25);
    padding: 6px 16px;
    border-radius: 30px;
    box-shadow: 0 0 12px rgba(255, 42, 42, 0.08);

    &::before {
      content: '⚡ ';
      color: #ff2a2a;
      font-size: 11px;
    }
  }

  .title {
    font-size: clamp(40px, 5vw, 60px);
    margin-bottom: 30px;
    color: var(--lightest-slate);

    .gradient-text {
      background: linear-gradient(90deg, #ffffff 10%, #ff2a2a 90%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      font-weight: 800;
    }
  }

  p {
    font-size: 18px;
    line-height: 1.6;
    color: var(--light-slate);
    max-width: 600px;
    margin: 0 auto 50px;

    &:last-child,
    &:last-of-type {
      margin: 0 auto 50px;
    }
  }

  .email-link {
    ${({ theme }) => theme.mixins.bigButton};
    font-size: 18px;
    padding: 1.5rem 3rem;
    border-radius: 4px;
    transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);

    @media (hover: hover) {
      &:hover,
      &:focus-visible {
        background: var(--green);
        color: var(--white);
        transform: scale(1.06);
        box-shadow: 0 10px 25px -10px var(--green);
      }
    }
    &:active {
      background: var(--green);
      color: var(--white);
      transform: scale(1.06);
      box-shadow: 0 10px 25px -10px var(--green);
    }
  }

  .small-text {
    font-size: 14px;
    margin-top: 20px;
    color: var(--slate);
    font-family: var(--font-mono);

    b {
      color: var(--green);
    }
  }
`;

const Contact = () => {
  const revealTitle = useRef(null);
  const revealCard = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }
    sr.reveal(revealTitle.current, srConfig());
    sr.reveal(revealCard.current, srConfig(200));
  }, [prefersReducedMotion]);

  return (
    <StyledContactSection id="contact">
      <h2 className="numbered-heading" ref={revealTitle}>
        Get In Touch
      </h2>

      <div className="contact-card" ref={revealCard}>
        <span className="overline">What’s Next?</span>
        <h2 className="title">
          <span className="gradient-text">Let’s Connect</span> 🤝
        </h2>
        <p>
          I’m currently open to new opportunities and exciting challenges! Whether you’re looking to
          collaborate, discuss a project, or just want to say hello - my inbox is always open. I’ll
          do my best to get back to you!
        </p>
        <a className="email-link" href={`mailto:${email}`} onClick={e => e.currentTarget.blur()}>
          Say Hello
        </a>
        <div className="small-text">
          Replies within <b>an hour</b>
        </div>
      </div>
    </StyledContactSection>
  );
};

export default Contact;
